import {CUSTOMER, SubAccount, SubAccountRelationType, SUPPLIER} from '@/subaccount/types/SubAccount'
import {CUSTOMER_ACCOUNT_NO, SUPPLIER_ACCOUNT_NO} from '@/bookkeeping-account/accountTypes'
import {BookkeepingAccount} from '@/bookkeeping-account/BookkeepingAccount'
import {computed, Ref, ref} from 'vue'
import Fuse from 'fuse.js'
import _ from 'lodash'

const mapAccountToAccountPickerOption = (account: BookkeepingAccount | SubAccount): AccountOption => {
    if ((account as SubAccount).relationType) {
        const subAccount = account as SubAccount
        const accountNo = subAccount.relationType === CUSTOMER ? CUSTOMER_ACCOUNT_NO : SUPPLIER_ACCOUNT_NO
        return {
            accountNo: accountNo,
            subKey: subAccount.subAccountNo,
            key: accountNo + ':' + subAccount.subAccountNo,
            accountType: subAccount.relationType === CUSTOMER ? AccountType.CustomerAccount : AccountType.SupplierAccount,
            displayName: subAccount.name,
            subAccountUuid: subAccount.uuid
        }
    } else if ((account as BookkeepingAccount).accountNo) {
        const bkae = account as BookkeepingAccount
        return {
            accountNo: bkae.accountNo,
            key: bkae.accountNo,
            accountType: AccountType.BookkeepingAccount,
            displayName: account.name
        }
    } else {
        throw new Error('Unexpected account type : ' + account)
    }
}

const accountOptionSorter = (first: AccountOption, second: AccountOption): number => {
    const accountNoCompare = first.accountNo.localeCompare(second.accountNo)
    if (accountNoCompare !== 0) return accountNoCompare
    return (first.subKey || 0) - (second.subKey || 0)
}


const evaluateFilter = (search: string, accountOptions: AccountOption[]) => {
    //Filter out BKAs for 1500 and 2400 without SACCs
    accountOptions = _.reject(accountOptions, ((acc: AccountOption) => {
        return acc.accountType === AccountType.BookkeepingAccount
            && (acc.accountNo === CUSTOMER_ACCOUNT_NO || acc.accountNo === SUPPLIER_ACCOUNT_NO)
    }))

    if (search.length === 0) return accountOptions

    const isAccountKeySearch = /^[0-9]{1,4}(:[0-9]{1,5})?$/.test(search)
    if (isAccountKeySearch) {
        return accountOptions.filter(option => option.key.startsWith(search))
    }

    const fuse = new Fuse(accountOptions, {keys: ['key', 'displayName'], threshold: 0.2, shouldSort: true})
    return fuse.search(search).map(res => res.item)
}

export const accountTypeToRelationType = (accountType: AccountType): SubAccountRelationType => {
    if (accountType === AccountType.BookkeepingAccount) {
        throw 'Unable to map bookeeping account to subAccountRelationType'
    }
    return accountType === AccountType.CustomerAccount ? CUSTOMER : SUPPLIER
}

export enum AccountType {
    BookkeepingAccount = 'BookkeepingAccount',
    CustomerAccount = 'CustomerAccount',
    SupplierAccount = 'SupplierAccount'
}

export enum FOCUS_SIDE {
    LEFT = 'left',
    RIGHT = 'right'
}

export type AccountOption = {
    accountNo: string,
    subKey?: number,
    key: string,
    accountType: AccountType,
    displayName: string,
    subAccountUuid?: string,
}

export const useAccountPicker = (accounts: Ref<Array<BookkeepingAccount | SubAccount>>, filterBySearch: Boolean) => {
    const accountOptions = computed<AccountOption[]>(() => accounts.value
        .map(mapAccountToAccountPickerOption)
        .sort(accountOptionSorter))
    const searchValue = ref('')

    const filteredOptions = computed(() => {
        if (filterBySearch) {
            return evaluateFilter(searchValue.value, accountOptions.value)
        }
        return accountOptions.value
    })

    return {
        searchValue,
        accountOptions,
        filteredOptions
    }
}